import Skeleton from '@passkeys/ui/skeleton';
import Text from '@passkeys/ui/text';
import TokenIcon from '@passkeys/ui/token-icon';
import type { ReactNode } from 'react';

import { trackMissingIcon } from '@/ui/sentry';
import { cn } from '@/ui/utils/classnames';
import { isNonNumber } from '@/ui/utils/content';
import { formatCrypto, formatFiat } from '@/ui/utils/formatting';
import ChainBadge from '../chain-badge/index.js';

export type TokenItemProps = {
  name: string;
  displayName: string;
  itemName: string | ReactNode;
  displayTicker: string;
  chainName?: string;
  balance: number | null | undefined;
  fiatValue: number | null | undefined;
  price?: number | null;
  stackedIcon?: boolean;
  loading?: boolean;
  className?: string;
};

function TokenItem({
  name,
  displayName,
  itemName,
  displayTicker,
  chainName,
  balance,
  fiatValue,
  price,
  stackedIcon = false,
  loading = false,
  className,
}: TokenItemProps) {
  return (
    <div
      className={cn(
        'grid w-full grid-flow-col grid-cols-[max-content_1fr_max-content]',
        className,
      )}
    >
      <div className="row-span-2">
        <TokenIcon
          name={name}
          displayName={displayName}
          size={40}
          stacked={stackedIcon}
          onUnknownName={(name) => trackMissingIcon(name, 'token')}
        />
      </div>

      <Text
        size={14}
        className="flex items-center justify-self-start"
        dataTestId="token-name"
      >
        {itemName}
      </Text>

      <div className="justify-self-start">
        {chainName ? (
          <ChainBadge name={chainName} />
        ) : loading || isNonNumber(price) ? (
          <Skeleton width={64} height={14} className="my-[3px]" />
        ) : (
          <Text size={14} className="text-gray-500 theme-dark:text-neutral-100">
            {formatFiat(price)}
          </Text>
        )}
      </div>

      <div className="justify-self-end">
        {isNonNumber(balance) ? (
          <Skeleton width={96} height={14} />
        ) : (
          <Text align="right" size={14} dataTestId="token-crypto-amount">
            {formatCrypto(balance, displayTicker)}
          </Text>
        )}
      </div>

      <div className="justify-self-end">
        {loading || isNonNumber(fiatValue) ? (
          <Skeleton width={48} height={14} className="my-[3px]" />
        ) : (
          <Text
            align="right"
            size={14}
            className="text-gray-400 theme-dark:text-neutral-200"
            dataTestId="token-fiat-amount"
          >
            {formatFiat(fiatValue)}
          </Text>
        )}
      </div>
    </div>
  );
}

export default TokenItem;
